/**
 * Read-through cache for trip data, so a trip you already opened still loads
 * with no connection.
 *
 * It lives in the axios layer and not in the service worker on purpose: a
 * Workbox runtime cache is keyed by URL alone, and several trip endpoints
 * answer DIFFERENT content at the same URL depending on who asks (packing
 * sections and private notes go through `visiblePackingSectionWhere`). Keying
 * an entry by its owner is only possible where the logged-in user is known,
 * which is here — in the service worker, two accounts sharing a browser would
 * read each other's private content out of the same cache entry.
 */

const DB_NAME = 'gromly-offline';
const DB_VERSION = 1;
const STORE = 'api-cache';
const OWNER_KEY = 'gromly.cacheOwner';

/**
 * Cache Storage bucket where the service worker keeps R2 documents and photos.
 * Must stay in sync with the `cacheName` in `vite.config.ts` — the build can't
 * import from here, and this is the only code that empties it.
 */
export const ASSET_CACHE = 'trip-assets';

export interface CacheEntry {
  data: unknown;
  /** ISO timestamp of the response that filled this entry. */
  savedAt: string;
}

let ownerId: string | null = readOwner();
let dbPromise: Promise<IDBDatabase | null> | null = null;

function readOwner(): string | null {
  try {
    return localStorage.getItem(OWNER_KEY);
  } catch {
    return null;
  }
}

function openDb(): Promise<IDBDatabase | null> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise<IDBDatabase | null>(resolve => {
    if (typeof indexedDB === 'undefined') {
      resolve(null);
      return;
    }
    let req: IDBOpenDBRequest;
    try {
      req = indexedDB.open(DB_NAME, DB_VERSION);
    } catch {
      resolve(null);
      return;
    }
    req.onupgradeneeded = () => req.result.createObjectStore(STORE);
    req.onsuccess = () => resolve(req.result);
    // Private browsing and a full disk both fail here. Losing the cache
    // degrades the offline experience; it must never break a live request.
    req.onerror = () => resolve(null);
    req.onblocked = () => resolve(null);
  });
  return dbPromise;
}

function withStore<T>(
  mode: IDBTransactionMode,
  run: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T | null> {
  return openDb().then(db => {
    if (!db) return null;
    return new Promise<T | null>(resolve => {
      let req: IDBRequest<T>;
      try {
        req = run(db.transaction(STORE, mode).objectStore(STORE));
      } catch {
        resolve(null);
        return;
      }
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => resolve(null);
    });
  });
}

function paramSuffix(params: unknown): string {
  if (!params || typeof params !== 'object') return '';
  const entries = Object.entries(params as Record<string, unknown>)
    .filter(([, v]) => v !== undefined && v !== null)
    .sort(([a], [b]) => a.localeCompare(b));
  if (!entries.length) return '';
  return `?${entries.map(([k, v]) => `${k}=${String(v)}`).join('&')}`;
}

function cacheKey(url: string, params: unknown): string | null {
  if (!ownerId) return null;
  return `${ownerId}|${url}${paramSuffix(params)}`;
}

async function purge(): Promise<void> {
  await withStore('readwrite', store => store.clear());
  if (typeof caches !== 'undefined') {
    await caches.delete(ASSET_CACHE).catch(() => false);
  }
}

/**
 * Binds the cache to a user. Switching accounts on the same browser drops
 * everything the previous one left behind — the owner prefix already keeps the
 * entries unreadable, but their documents and photos shouldn't stay on disk.
 */
export function setCacheOwner(userId: string): void {
  if (ownerId === userId) return;
  if (ownerId) void purge();
  ownerId = userId;
  try {
    localStorage.setItem(OWNER_KEY, userId);
  } catch {
    /* storage disabled: the cache just stops working */
  }
}

export function clearOfflineCache(): Promise<void> {
  ownerId = null;
  try {
    localStorage.removeItem(OWNER_KEY);
  } catch {
    /* ignore */
  }
  return purge();
}

/**
 * Drops the Workbox buckets that used to cache API responses by URL. They
 * should be empty — the patterns were path-only, so they never matched the
 * cross-origin API — but `cleanupOutdatedCaches` only touches precaches, and
 * "should be empty" is not what you want to rely on for per-viewer content.
 * Safe to delete once every installed client has run a build past 2026-08-20.
 */
export function dropLegacyApiCaches(): void {
  if (typeof caches === 'undefined') return;
  for (const name of ['itinerary-cache', 'bookings-cache', 'trips-cache']) {
    void caches.delete(name).catch(() => false);
  }
}

export async function readApiCache(url: string, params: unknown): Promise<CacheEntry | null> {
  const key = cacheKey(url, params);
  if (!key) return null;
  const entry = await withStore<CacheEntry | undefined>('readonly', store => store.get(key));
  return entry ?? null;
}

export async function writeApiCache(url: string, params: unknown, data: unknown): Promise<void> {
  const key = cacheKey(url, params);
  if (!key) return;
  await withStore('readwrite', store =>
    store.put({ data, savedAt: new Date().toISOString() } satisfies CacheEntry, key),
  );
}
